/**
 * Markdown rendering for agent messages.
 *
 * Uses snarkdown (~1 KB) for lightweight Markdown → HTML conversion.
 * Applies a sanitization pass to neutralize dangerous href schemes and
 * enforce safe link attributes. The backend already strips raw HTML from
 * agent output, so the only real attack surface is markdown-generated links.
 */

import snarkdown from 'snarkdown';

/**
 * Neutralize dangerous href schemes and enforce `target="_blank"` on all
 * anchor tags produced by snarkdown.
 *
 * Threats addressed:
 *   - `javascript:` hrefs → replaced with `#`
 *   - `data:` hrefs → replaced with `#`
 *   - `vbscript:` hrefs → replaced with `#`
 *   - Missing `target`/`rel` → added for all anchors
 */
export function sanitizeMarkdownHtml(html: string): string {
  // 1. Neutralize dangerous href schemes (case-insensitive, allow whitespace/tabs)
  const sanitized = html.replace(
    /(<a\s[^>]*href\s*=\s*["'])([^"']*)(["'][^>]*>)/gi,
    (_match, before, href, after) => {
      const trimmed = href.trim().toLowerCase();
      const isDangerous =
        trimmed.startsWith('javascript:') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('vbscript:');
      return `${before}${isDangerous ? '#' : href}${after}`;
    },
  );

  // 2. Add target="_blank" rel="noopener noreferrer" to every <a> tag
  //    that does not already have a target attribute.
  return sanitized.replace(/<a\s([^>]*)>/gi, (_match, attrs) => {
    const hasTarget = /\btarget\s*=/i.test(attrs);
    const hasRel = /\brel\s*=/i.test(attrs);
    let result = attrs;
    if (!hasTarget) result += ' target="_blank"';
    if (!hasRel) result += ' rel="noopener noreferrer"';
    return `<a ${result.trim()}>`;
  });
}

/**
 * Turn the newlines snarkdown leaves behind into `<br />`.
 *
 * snarkdown only emits a `<br />` for a blank line (`\n{2,}`) or an explicit
 * two-space hard break; a *single* `\n` survives verbatim into the HTML, where
 * the browser collapses it to a space. Agents write menus and step lists with
 * one newline per item, so those arrived as a single run-on paragraph:
 *
 *   "1) Timbrado\n2) Cancelar\n3) Recibo"  →  "1) Timbrado 2) Cancelar 3) Recibo"
 *
 * Every other chat surface (WhatsApp, the inbox) honors a single newline, so
 * the same agent text has to read the same way here. `<pre>` blocks are skipped
 * — newlines are already significant inside them.
 */
function newlinesToBreaks(html: string): string {
  return html
    .split(/(<pre[\s\S]*?<\/pre>)/)
    .map((chunk, i) => (i % 2 === 1 ? chunk : chunk.replace(/\n/g, '<br />')))
    .join('');
}

/**
 * URL detection, mirroring the inbox's `linkifyText`
 * (`mirlo-frontend` -> `src/dashboard/Chat/utils/link-helpers.ts`) so the same
 * agent text reads the same way on both surfaces: full `http(s)://` URLs plus
 * the protocol-less hosts people actually paste (`www.`, shorteners, maps).
 *
 * The inbox stops at `[^\s<>]`. This one also stops at a quote, because the
 * match is dropped straight into an `href="..."` attribute, and at a backtick,
 * `]` or `*`, because here the URL is pulled out *before* the Markdown is
 * rendered: swallowing the closing delimiter of a code span, a `[texto](url)`
 * or a bold run would leave that markup unterminated.
 */
const URL_STOP = '[^\\s<>"\'`\\]*]';
const URL_SOURCE =
  `https?:\\/\\/${URL_STOP}+|(?:www\\.|maps\\.|goo\\.gl|bit\\.ly|tinyurl\\.com|t\\.co|` +
  `youtu\\.be|youtube\\.com|instagram\\.com|facebook\\.com|twitter\\.com|x\\.com|` +
  `linkedin\\.com)${URL_STOP}+`;

/**
 * A bare URL, or one wrapped in Markdown's `<https://...>` autolink brackets.
 * The brackets are part of the match so they can be dropped: snarkdown passes
 * them through verbatim and the browser then swallows `<https://mirlo.com>` as
 * an unknown element, which made the URL vanish from the bubble entirely.
 */
const URL_RE = new RegExp(`<(?:${URL_SOURCE})>|${URL_SOURCE}`, 'gi');

/** Elements whose text must never be turned into a link. */
const NO_LINKIFY_TAGS = new Set(['a', 'pre', 'code']);

/**
 * NUL-delimited stand-in for a URL while snarkdown runs: `<NUL>u<n><NUL>`.
 * NUL is the one character that cannot survive in agent text (it is stripped
 * from the input below), so no message can forge a token of its own.
 */
const NUL = String.fromCharCode(0);
const TOKEN_RE = new RegExp(`${NUL}u(\\d+)${NUL}`, 'g');

/**
 * Drop the closing punctuation a sentence leaves glued to a URL, so
 * "entra a https://mirlo.com/ayuda." does not produce a link to `/ayuda.`.
 *
 * Simple punctuation and quotes always go. A closing `)` only goes when it is
 * unbalanced within the URL itself, which keeps legitimate Wikipedia-style
 * links (`/wiki/Mirlo_(ave)`) intact.
 */
function stripTrailingPunctuation(url: string): string {
  let result = url;

  for (let changed = true; changed && result.length > 0; ) {
    changed = false;

    const withoutSimple = result.replace(/[.,;:!?'"]+$/, '');
    if (withoutSimple !== result) {
      result = withoutSimple;
      changed = true;
    }

    if (result.endsWith(')')) {
      const opens = result.split('(').length - 1;
      const closes = result.split(')').length - 1;
      if (closes > opens) {
        result = result.slice(0, -1);
        changed = true;
      }
    }
  }

  return result;
}

/**
 * Swap every URL out for an opaque token before snarkdown sees the text.
 *
 * A URL is not Markdown, but it is full of characters snarkdown treats as
 * markup: `https://es.wikipedia.org/wiki/Mirlo_(ave)` came out with the
 * underscore eaten and an `<em>` in the middle of the address. Masking first
 * is what keeps the URL intact -- for bare URLs *and* for the ones inside
 * `[texto](url)`, where the token simply rides along into the `href`.
 */
function maskUrls(text: string): { masked: string; urls: string[] } {
  const urls: string[] = [];

  const masked = text
    .split(NUL)
    .join('')
    .replace(URL_RE, (match) => {
      const body = match.startsWith('<') ? match.slice(1, -1) : match;
      const url = stripTrailingPunctuation(body);
      if (url.length === 0) return match;

      const token = `${NUL}u${urls.push(url) - 1}${NUL}`;
      // Whatever the strip removed was sentence punctuation, not part of the
      // address: it stays in the text, outside the link.
      return token + body.slice(url.length);
    });

  return { masked, urls };
}

/**
 * Put the URLs back, as links where a link belongs.
 *
 * snarkdown only builds an anchor out of `[texto](url)`, so an agent that
 * writes the URL plainly -- the normal case, since the same text also goes to
 * WhatsApp, where the client linkifies it -- produced an unclickable bubble.
 *
 * Tokens are restored verbatim inside tags (that is an `href` the author
 * already wrote) and inside `<a>`, `<pre>` and `<code>`, where a link would be
 * either nested or unwanted. Everywhere else they become anchors.
 */
function restoreUrls(html: string, urls: string[]): string {
  if (urls.length === 0) return html;

  const parts = html.split(/(<[^>]*>)/);
  let skipDepth = 0;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.length === 0) continue;

    const tag = /^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)(?=[\s/>])/.exec(part);
    const asLink = tag === null && skipDepth === 0;

    parts[i] = part.replace(TOKEN_RE, (token, index) => {
      const url = urls[Number(index)];
      if (url === undefined) return token;
      if (!asLink) return url;
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return `<a href="${href}">${url}</a>`;
    });

    if (tag !== null && NO_LINKIFY_TAGS.has(tag[2].toLowerCase())) {
      skipDepth = Math.max(0, skipDepth + (tag[1] === '/' ? -1 : 1));
    }
  }

  return parts.join('');
}

/**
 * Stand-ins for characters that must reach the bubble as plain text, not as
 * markup: `<NUL>l<n><NUL>`, restored after snarkdown by `restoreLiterals`.
 */
const LITERALS = ['*', '_', '[', ']'];
const LITERAL_RE = new RegExp(`${NUL}l(\\d)${NUL}`, 'g');
const literal = (char: string) => `${NUL}l${LITERALS.indexOf(char)}${NUL}`;

/**
 * Code spans and fenced blocks: whatever is inside them is already literal
 * for snarkdown, so the WhatsApp pass below leaves them alone.
 */
const CODE_RE = /(```[\s\S]*?```|`[^`\n]*`)/;

/** A real Markdown link or image, the only use of brackets snarkdown gets right. */
const MD_LINK_RE = /(!?\[[^\]\n]*\]\([^)\n]*\))/;

/**
 * WhatsApp emphasis, with the same pairing guards as the inbox renderer
 * (`mirlo-frontend` -> `src/dashboard/Chat/utils/whatsappFormat.ts`): the
 * opening marker is followed by non-space, the closing one preceded by
 * non-space, and the span never crosses a newline. The leading `(^|[^*])`
 * group stands in for a lookbehind, which older Safari cannot parse and would
 * take the whole widget down with a SyntaxError.
 */
const MD_BOLD_RE = /(\*\*|__)(?![\s*_])([^\n]*?[^\s*_])\1/g;
const WA_BOLD_RE = /(^|[^*])\*(?![\s*])([^*\n]*[^*\s]|[^*\s])\*(?!\*)/g;
const WA_ITALIC_RE = /(^|[^_])_(?![\s_])([^_\n]*[^_\s]|[^_\s])_(?!_)/g;

/** Open/close stand-ins for a paired span while the leftovers are escaped. */
const BOLD_OPEN = `${NUL}B${NUL}`;
const BOLD_CLOSE = `${NUL}b${NUL}`;
const EM_OPEN = `${NUL}E${NUL}`;
const EM_CLOSE = `${NUL}e${NUL}`;

/**
 * Read the agent's text the way WhatsApp and the inbox do, before snarkdown
 * reads it as Markdown.
 *
 * Agents write for WhatsApp, because the same text goes out on every channel:
 * `*texto*` means bold and `[NOMBRE]` is a placeholder to fill in. snarkdown
 * got both wrong, and the second one broke the rest of the message:
 *
 *   - `*texto*` came out italic instead of bold.
 *   - `[NOMBRE]` without a `(url)` became `<a href="undefined">`, and closing
 *     that anchor flushed snarkdown's emphasis state mid-run: from the first
 *     `*[NOMBRE]*` on, every italic span was inverted, all the way down.
 *   - One stray `*` or `_` (a footnote, `layout_final.xlsx`) opened an `<em>`
 *     that never closed.
 *
 * Paired spans become Markdown bold/italic; every marker left without a pair,
 * and every bracket that is not part of a `[texto](url)`, is swapped for a
 * literal stand-in so snarkdown shows it as the character it is -- the same
 * fidelity rule as the inbox. `* item` at the start of a line is still a list
 * bullet.
 */
function applyWhatsAppFormat(text: string): string {
  return text
    .split(CODE_RE)
    .map((chunk, i) => {
      if (i % 2 === 1) return chunk;

      return chunk
        .split(MD_LINK_RE)
        .map((part, j) =>
          j % 2 === 1 ? part : part.replace(/[[\]]/g, literal),
        )
        .join('')
        .replace(MD_BOLD_RE, `${BOLD_OPEN}$2${BOLD_CLOSE}`)
        .replace(WA_BOLD_RE, `$1${BOLD_OPEN}$2${BOLD_CLOSE}`)
        .replace(WA_ITALIC_RE, `$1${EM_OPEN}$2${EM_CLOSE}`)
        .replace(/^([ \t]*)\*(?=[ \t])/gm, `$1${NUL}bullet${NUL}`)
        .replace(/[*_]/g, literal)
        .split(`${NUL}bullet${NUL}`)
        .join('*')
        .split(BOLD_OPEN)
        .join('**')
        .split(BOLD_CLOSE)
        .join('**')
        .split(EM_OPEN)
        .join('_')
        .split(EM_CLOSE)
        .join('_');
    })
    .join('');
}

/** Put back the characters `applyWhatsAppFormat` kept away from snarkdown. */
function restoreLiterals(html: string): string {
  return html.replace(LITERAL_RE, (_token, index) => LITERALS[Number(index)]);
}

/**
 * Convert a Markdown string (from an agent message) to safe HTML.
 * Returns an object suitable for Preact's `dangerouslySetInnerHTML`.
 */
export function renderAgentMarkdown(text: string): { __html: string } {
  const { masked, urls } = maskUrls(text);
  const html = snarkdown(applyWhatsAppFormat(masked));
  const raw = restoreLiterals(restoreUrls(newlinesToBreaks(html), urls));
  return { __html: sanitizeMarkdownHtml(raw) };
}
